import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { OpaqueVideoData, Scene } from '@lumen5/luminary';
import Luminary, { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import { ApiService, type Blueprint } from '../../client';
import type { EnhancedToolResult, ToolExecutionContext } from '../types';
import { generateVideoDescriptionFromVideoData } from '../video-state';
import { NO_VIDEO_STATE_MESSAGE } from '../video-state/constants';

import { MediaRegistry } from '../registries/MediaRegistry';

import { extractReferenceInsights } from './reference-video-insights';
import { buildReferenceStateGetter } from './reference-video-transcript';
import { Tool } from './Tool';
import { VideoStateTool } from './VideoStateTool';

import { getOrFetchBlueprint } from 'js/src/data/blueprints/blueprints-operations';
import { startSceneBuilding } from 'js/src/data/agent-activity';
import { loadTemplateBundle } from 'js/src/data/templates/templates-operations';
import { getTemplateBundle } from 'js/src/data/templates/templates-selectors';
import { checkpointVideo } from 'js/src/data/video-data';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import { isLuminaryEmpty } from 'js/src/luminary/luminary-selectors';
import type { RootState } from 'js/src/libs/RootState';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { enableOutroSceneGroupOperation } from 'js/src/luminary/luminary-operations';
import { saveRawLuminary } from 'js/src/luminary/utils';
import { isApiError } from 'js/src/types';

export const fetchReferenceVideoSchema = z.object({
    video_id: z
        .number()
        .optional()
        .describe(
            'ID of the video to use as the reference. Use when the user points at a specific video rather than a blueprint. Do not pass together with blueprint_id.',
        ),
    blueprint_id: z
        .number()
        .optional()
        .describe(
            'The blueprint whose reference video to fetch. When the user has selected a blueprint, this is provided in their preferences. Do not pass together with video_id.',
        ),
    apply_global_styles: z
        .boolean()
        .optional()
        .describe("Copy the reference's video styling + outro to your video."),
});
export type FetchReferenceVideoArgs = z.infer<typeof fetchReferenceVideoSchema>;

const REFERENCE_VIDEO_HEADER =
    '## Reference Video State\n' +
    '_This is the reference video — the video to imitate. It is NOT the video you are editing._\n' +
    // Explained here rather than in the always-on asset legend: the marker is only ever emitted
    // for a reference video, so documenting it globally would describe a suffix most agents never
    // see. A value with no marker is simply the scene design's own default.
    "_A property marked `(changed from design default X)` is one this video's author set " +
    'deliberately, where `X` is what its scene design would otherwise have given. Those are the ' +
    'values to reproduce; unmarked ones your own design already supplies._\n\n';

const REFERENCE_MEDIA_HEADER =
    '## Reference Media\n' +
    `_The reference video's media, indexed like search results. Reuse an id with ${TOOL_NAMES.setMedia} where ` +
    'the very same asset belongs in the new video (a logo, a brand clip, an outro image); for ' +
    'everything else, search for media in the same style as these._\n';

/**
 * The reference's template settings, extracted the way `applyBlueprintTemplateSettingsOperation`
 * does it: load the video into a throwaway engine and save its template back out, so every setting
 * the template owns comes across rather than a hand-picked subset. Null when it has no template.
 */
function loadReferenceTemplate(
    referenceVideoData: OpaqueVideoData,
): ReturnType<Luminary['template']['save']> | null {
    const dataLuminary = new Luminary({
        fetchResources: false,
        disableRenderer: true,
        autoDraw: false,
        autoUpdate: false,
    });
    try {
        dataLuminary.load(referenceVideoData);
        return dataLuminary.template.id ? dataLuminary.template.save() : null;
    } catch (error) {
        ErrorReporter.capture(error, { fingerprint: ['fetch-reference-video-template-load'] });
        return null;
    } finally {
        dataLuminary.destroy();
    }
}

/** Distinguishes the reference's snapshot from the turn's own before/after video snapshots. */
const REFERENCE_SNAPSHOT_LABEL = 'Reference video';

const STYLING_ALREADY_APPLIED =
    "No change: the reference's template styling is already on your video.";

/**
 * Whether a styling outcome means the video now carries the reference's styling — freshly applied
 * or already there. `applyGlobalStyles` reports its failures in the same string it reports success
 * in, so the prefix is what distinguishes them.
 */
const stylingSucceeded = (styleOutcome: string): boolean =>
    styleOutcome.startsWith("Applied the reference's") || styleOutcome === STYLING_ALREADY_APPLIED;

/**
 * The annotation, unless this reference's annotation has already been served and the styling just
 * landed.
 *
 * The skill has the agent fetch the reference once to study it and again after creating the video
 * to copy its styling across; returning the full annotation both times duplicated tens of thousands
 * of tokens it was already holding. Suppression is keyed on having ACTUALLY served that reference
 * before, not merely on this being a styling call — agents routinely skip the study call and go
 * straight to `apply_global_styles`, and withholding the annotation there left them building a
 * look-alike of a video they had never seen.
 */
const bodyForStyleOutcome = (
    styleOutcome: string,
    annotation: string,
    alreadyServed: boolean,
): string =>
    alreadyServed && stylingSucceeded(styleOutcome)
        ? '\n\n_Annotation omitted — it is unchanged from your earlier fetch of this reference._'
        : `\n\n${annotation}`;

/**
 * The reference's media as the agent sees search results — same registry representations, so the
 * ids are directly usable. Empty when the reference carries no media.
 */
function formatReferenceMedia(mediaIds: string[]): string {
    const mediaRegistry = MediaRegistry.getInstance();
    const representations = mediaIds
        .map((id) => mediaRegistry.getRepresentation(id))
        .filter((representation) => representation !== null);
    if (representations.length === 0) {
        return '';
    }
    return `\n\n${REFERENCE_MEDIA_HEADER}${JSON.stringify(representations)}`;
}

function formatBlueprintSettings(blueprint: Blueprint): string {
    const lines: string[] = [
        `- Default template: ${blueprint.default_template_id}`,
        `- Default aspect ratio: ${blueprint.default_aspect_ratio}`,
        `- Narrative thread: ${blueprint.narrative_thread}`,
        `- Target duration: ${blueprint.default_target_duration_seconds} seconds`,
    ];
    if (blueprint.ai_voice) {
        lines.push(`- AI voice: ${blueprint.ai_voice}`);
    }
    if (blueprint.description) {
        lines.push(`- Description: ${blueprint.description}`);
    }
    if (blueprint.supported_templates.length > 0) {
        lines.push(
            `- Templates with reference examples: ${blueprint.supported_templates.join(', ')}`,
        );
    }
    return lines.join('\n');
}

/**
 * Fetches a reference video — either a blueprint's example video or any video
 * the user can access — and returns its full annotation, so the agent can
 * build a new video that matches the reference's script style and visual
 * choices. The blueprint path reads the example through the blueprint-scoped
 * route, so the blueprint itself grants access to it, and additionally returns
 * the blueprint's settings and custom instructions.
 */
export class FetchReferenceVideoTool extends Tool<typeof fetchReferenceVideoSchema> {
    protected override readonly schema = fetchReferenceVideoSchema;
    protected override readonly toolName = TOOL_NAMES.fetchReferenceVideo;
    protected override readonly toolDescription =
        "Fetch a reference video's complete scene-by-scene annotation (script text, scene designs, media, durations) to imitate its style. Pass blueprint_id when the user selected a blueprint (also returns the blueprint's settings and custom instructions); pass video_id to reference one of the user's own videos directly. Call this BEFORE building a video that should match an existing video's style.";

    /**
     * References whose annotation this tool has already returned, keyed per reference — a video by
     * its id, a blueprint example by the blueprint and template it came from, since that route
     * identifies the example rather than exposing its video id. The agent keeps its whole
     * transcript, so an annotation it was sent once is still in context — but only one it was
     * actually sent.
     */
    private readonly servedAnnotations = new Set<string>();

    public override getToolDisplayName(): string {
        return 'Studying reference video';
    }

    public get statusIcon(): string {
        return 'video_library';
    }

    public async execute(
        args: FetchReferenceVideoArgs,
        _context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const { video_id: videoId, blueprint_id: blueprintId } = args;

        if (videoId && blueprintId) {
            return {
                result: 'Error: Pass either video_id or blueprint_id, not both.',
                isSuccess: false,
            };
        }
        const applyStyles = args.apply_global_styles === true;
        if (videoId) {
            return this.fetchByVideoId(videoId, applyStyles);
        }
        if (blueprintId) {
            return this.fetchByBlueprintId(blueprintId, applyStyles);
        }
        return {
            result: 'Error: Either video_id or blueprint_id is required.',
            isSuccess: false,
        };
    }

    /**
     * Serializes the reference video into its annotation, or null when the
     * video has no scenes (the serializer's "no video yet" message describes
     * the agent's own video, which is exactly what a reference must not be
     * confused with).
     */
    private async describeReference(
        videoData: OpaqueVideoData,
        transcripts?: Record<string, Record<string, unknown>>,
        templateBundle?: ReturnType<typeof getTemplateBundle>,
    ): Promise<string | null> {
        // Overlay the reference video's transcript editors onto the session state so its narration
        // renders with per-scene coverage and pauses — identical to get_video_state.
        const getState = buildReferenceStateGetter(this.getState(), transcripts);
        let referenceMediaIds: string[] = [];
        let insights = '';
        const annotation = await generateVideoDescriptionFromVideoData(videoData, {
            header: REFERENCE_VIDEO_HEADER,
            getState,
            // This video is being imitated, not edited, so which of its values its author chose
            // deliberately is exactly what the agent needs to reproduce.
            markDesignDefaults: true,
            ...(templateBundle ? { templateBundle } : {}),
            // Index the reference's media before it is serialized, so every media asset in the
            // annotation carries a real title and an id set_media accepts. Built from the assets'
            // own source data: a public blueprint's example lives in another workspace, where
            // fetching its media by uuid would be denied.
            onLoaded: (luminary) => {
                referenceMediaIds =
                    MediaRegistry.getInstance().addUnseenItemsFromSourceData(luminary);
                // Same loaded engine the serializer uses, so the summary is read off exactly the
                // state the annotation describes.
                insights = extractReferenceInsights(luminary);
            },
        });
        if (annotation === NO_VIDEO_STATE_MESSAGE) {
            return null;
        }
        // The summary leads: the rules it states are the ones most often missed when they have to
        // be inferred from the scene dump that follows.
        return `${insights}\n${annotation}${formatReferenceMedia(referenceMediaIds)}`;
    }

    /**
     * The reference's own template bundle, fetched the way opening a video does.
     *
     * The serializer replays the reference into a throwaway engine, and `load()` brings across only
     * the scene designs the video actually used — not the template's catalog — so without the
     * bundle no design resolves and every value the reference's author deliberately styled
     * serializes indistinguishably from one that just inherited the design's default. Null on any
     * failure: a template that can't be fetched costs those markers, never the annotation.
     */
    private async loadReferenceBundle(
        templateId: string | null | undefined,
    ): Promise<ReturnType<typeof getTemplateBundle>> {
        if (!templateId) {
            return null;
        }
        try {
            await this.dispatch(loadTemplateBundle(templateId));
            return getTemplateBundle(this.getState() as RootState, templateId);
        } catch (error) {
            // Not reported to Sentry: a reference can legitimately sit on a template this session
            // can't fetch (a prod-only one, a workspace the user lost access to), and the fallback
            // is benign — the annotation still goes out, only without the design-default markers.
            // The eval harness takes the same line for the same reason (see createToolboxAgent).
            console.warn(
                `[${TOOL_NAMES.fetchReferenceVideo}] could not load template bundle "${templateId}"; ` +
                    'the reference annotation will omit its design-default markers.',
                error,
            );
            return null;
        }
    }

    private async fetchAndDescribe(
        videoId: number,
    ): Promise<{ name: string; annotation: string | null; videoData: OpaqueVideoData }> {
        const videoResponse = await ApiService.videoRetrieve({
            path: { video_id: String(videoId) },
        });
        const video = videoResponse.data;
        const videoData = video.video_data as OpaqueVideoData;
        const templateBundle = await this.loadReferenceBundle(video.template_id);

        return {
            name: video.name,
            annotation: await this.describeReference(videoData, video.transcripts, templateBundle),
            videoData,
        };
    }

    /** A blueprint's example, fetched through the blueprint-scoped route. */
    private async fetchAndDescribeBlueprintExample(
        blueprintId: number,
        templateId: string,
    ): Promise<{ annotation: string | null; videoData: OpaqueVideoData }> {
        const response = await ApiService.blueprintsTemplateStyleVideoDataRetrieve({
            path: { id: blueprintId },
            query: { template: templateId },
        });
        // This route serializes video_data as a JSON string, unlike the per-video route.
        const videoData = JSON.parse(response.data.video_data) as OpaqueVideoData;
        const templateBundle = await this.loadReferenceBundle(templateId);

        return {
            annotation: await this.describeReference(
                videoData,
                response.data.transcripts,
                templateBundle,
            ),
            videoData,
        };
    }

    /**
     * Put the outro through the same build-tracking `add_scene` gives the one it creates, so the
     * toolbox grid shows it loading rather than reading as finished the moment it appears — its
     * copied media is still resolving at that point.
     *
     * Nothing completes it: the content is copied in the same breath as the creation, so no later
     * `set_text`/`set_media` fill arrives for it. That matches every other scene whose design ships
     * its slots already filled — the turn's own `clearAllSceneBuilding`/`clearAllAgentActivity` is
     * what releases it, which is the point at which an unfilled scene is finished rather than
     * pending.
     */
    private trackOutroBuilding(outroScene: Scene): void {
        const { needsText, needsMedia, needsIcon } =
            VideoStateTool.getSceneFillRequirements(outroScene);
        this.dispatch(
            startSceneBuilding({ sceneId: outroScene.id, needsText, needsMedia, needsIcon }),
        );
    }

    /**
     * Recreate the reference's outro scene on the video being edited, when it has one.
     *
     * An outro is branding rather than content — the same logo, URL and colors close every video
     * made from a reference — so it belongs with the global styling rather than being rebuilt from
     * scratch per video. Luminary allows exactly one outro and owns its placement, so this routes
     * through `enableOutroSceneGroup` and then copies the reference outro's text and media onto the
     * scene it creates. Returns a note for the tool result, or an empty string when there is
     * nothing to copy.
     */
    private applyReferenceOutro(
        referenceVideoData: OpaqueVideoData,
        referenceTemplate: OpaqueVideoData,
    ): string {
        const referenceOutro = (
            referenceVideoData as { slides?: { sceneCategory?: string; sceneDesign?: string }[] }
        ).slides?.find((slide) => slide.sceneCategory === VideoData.CATEGORY_OUTRO);
        if (!referenceOutro?.sceneDesign) {
            return '';
        }

        // Luminary allows exactly one outro and owns where it sits, so create it through the same
        // operation the editor uses rather than inserting a scene group by hand.
        const { enableOutroSceneGroup } = makeOperationsCallable(
            { enableOutroSceneGroupOperation },
            this.luminary,
            this.dispatch,
            this.getState as () => RootState,
        );
        const targetTemplate = this.luminary.template.save();
        const [referenceTemplateId] = referenceOutro.sceneDesign.split(':');
        // A reference on another template names a design this video doesn't have; fall back to the
        // target template's own default outro so the scene still gets created.
        const sceneDesignId =
            referenceTemplateId === this.luminary.template.id
                ? referenceOutro.sceneDesign
                : undefined;

        try {
            enableOutroSceneGroup(false, undefined, sceneDesignId);
            const outro = this.luminary.getOutroSceneGroup();
            if (!outro) {
                return '';
            }
            // duplicateSceneData remaps the scene's `themeRef_` colour, font and watermark
            // references onto the target template — the reason to use it rather than copying assets
            // across by hand, which would leave references pointing at slots that may not exist.
            outro.scene.load(
                VideoData.duplicateSceneData(
                    referenceOutro as OpaqueVideoData,
                    referenceTemplate,
                    outro.scene.sceneDesign,
                    targetTemplate,
                ),
            );
            this.trackOutroBuilding(outro.scene);
            return `outro scene (${outro.scene.sceneDesign})`;
        } catch (error) {
            ErrorReporter.capture(error, {
                fingerprint: ['fetch-reference-video-outro'],
                extra: { sceneDesignId },
            });
            return '';
        }
    }

    /**
     * Copy the reference's global styling onto the video being edited: its whole template (colors,
     * fonts, watermark) plus its outro scene.
     *
     * `template.load()` is the same call `applyBlueprintTemplateSettingsOperation` uses to put a
     * blueprint's styling on a video, so a reference video styles a video exactly the way a
     * blueprint does. Per-scene content stays the agent's job; the outro is the exception because
     * it is branding — the same logo and closing line end every video made from the reference.
     */
    private applyGlobalStyles(referenceVideoData: OpaqueVideoData): string {
        // A template bundle, not just a scene, is required — `create_blank_video` produces one
        // with no scenes yet, and that is exactly when the agent should be styling. Guarding on
        // scenes (as an earlier version did) rejects the correct call and silently leaves the
        // video unstyled. Guarding on `template?.id` alone is not enough either: the id defaults
        // to a random uuid before any bundle is ever loaded, so it's truthy even when there is no
        // real template to style — which let this proceed into `applyReferenceOutro`, whose
        // `enableOutroSceneGroup` call needs the bundle's scene designs and throws without them.
        if (!this.luminary.isTemplateBundleSet) {
            return (
                'Error: apply_global_styles needs a video to style — create the video first, then ' +
                'call this again with apply_global_styles to copy the reference styling onto it.'
            );
        }

        const applied: string[] = [];
        let templateAlreadyApplied = false;
        const referenceTemplate = loadReferenceTemplate(referenceVideoData);
        if (referenceTemplate) {
            // Compared through save() on both sides, so the reference is normalised the same way
            // the video's own template is rather than diffed against its raw serialized form.
            const templateBefore = JSON.stringify(this.luminary.template.save());
            this.luminary.template.load(referenceTemplate);
            templateAlreadyApplied =
                JSON.stringify(this.luminary.template.save()) === templateBefore;
            if (!templateAlreadyApplied) {
                applied.push('template styling');
            }
        }

        const outroNote = referenceTemplate
            ? this.applyReferenceOutro(referenceVideoData, referenceTemplate as OpaqueVideoData)
            : '';
        if (outroNote) {
            applied.push(outroNote);
        }

        if (applied.length === 0) {
            return templateAlreadyApplied
                ? STYLING_ALREADY_APPLIED
                : 'Note: the reference carries no global styling to apply.';
        }
        const appliedSummary = `Applied the reference's ${applied.join(' and ')} to your video.${
            templateAlreadyApplied ? ' Its template styling was already on your video.' : ''
        }`;

        // The styling is already on the luminary; the checkpoint only records it for undo. A
        // scene-less video cannot be serialized at all (saveRawLuminary rejects it), and that is
        // the normal case here — styling right after create_blank_video. Whatever adds the first
        // scene checkpoints this change along with it.
        if (isLuminaryEmpty(this.luminary)) {
            return appliedSummary;
        }
        // Any other serialization failure (a video mid-edit) must not lose the styling or fail the
        // fetch either, so report it and carry on.
        try {
            this.dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(this.luminary),
                    systemTriggered: false,
                    parentActionGroup: undefined,
                }),
            );
        } catch (error) {
            ErrorReporter.capture(error, {
                fingerprint: ['fetch-reference-video-style-checkpoint'],
            });
        }
        return appliedSummary;
    }

    private async fetchByVideoId(
        videoId: number,
        applyStyles: boolean,
    ): Promise<EnhancedToolResult> {
        const { name, annotation, videoData } = await this.fetchAndDescribe(videoId);
        if (annotation === null) {
            return {
                result: `Error: Reference video "${name}" (id ${videoId}) has no scenes to imitate. Ask the user for a different reference video, or build without one.`,
                isSuccess: false,
            };
        }

        const styleOutcome = applyStyles ? this.applyGlobalStyles(videoData) : '';
        const styleNote = styleOutcome ? `\n\n${styleOutcome}` : '';
        const alreadyServed = this.servedAnnotations.has(`video:${videoId}`);
        this.servedAnnotations.add(`video:${videoId}`);

        return {
            result: `Success. Fetched reference video "${name}" (id ${videoId}).${styleNote}${bodyForStyleOutcome(styleOutcome, annotation, alreadyServed)}`,
            isSuccess: true,
            videoDataSnapshot: videoData,
            videoDataSnapshotLabel: REFERENCE_SNAPSHOT_LABEL,
        };
    }

    private async fetchByBlueprintId(
        blueprintId: number,
        applyStyles: boolean,
    ): Promise<EnhancedToolResult> {
        const blueprint = await this.dispatch(getOrFetchBlueprint(blueprintId));
        if (!blueprint) {
            return {
                result: `Error: Blueprint ${blueprintId} was not found or is not accessible.`,
                isSuccess: false,
            };
        }

        // The agent imitates the example's style, not its template, so any of the blueprint's
        // examples will do — take the default one, falling back to whichever exists. The fetch
        // selects the example by template, so what's needed here is the key rather than the id.
        const exampleTemplateId =
            blueprint.supported_templates_data[blueprint.default_template_id] !== undefined
                ? blueprint.default_template_id
                : Object.keys(blueprint.supported_templates_data)[0];
        if (exampleTemplateId === undefined) {
            return {
                result: `Error: Blueprint ${blueprintId} ("${blueprint.name}") has no reference video. Ask the user for a different reference`,
                isSuccess: false,
            };
        }

        let referenceAnnotation: string | null;
        let referenceVideoData: OpaqueVideoData;
        try {
            ({ annotation: referenceAnnotation, videoData: referenceVideoData } =
                await this.fetchAndDescribeBlueprintExample(blueprintId, exampleTemplateId));
        } catch (error) {
            // The blueprint route grants the example via the blueprint, so a refusal here means the
            // user isn't entitled to this blueprint's example at all (a workspace blueprint whose
            // example sits outside their projects) — report it rather than papering over it.
            if (isApiError(error) && (error.status === 403 || error.status === 404)) {
                ErrorReporter.capture(error, {
                    fingerprint: ['fetch-reference-video-blueprint-example-denied'],
                    extra: {
                        blueprintId,
                        exampleTemplateId,
                        isPublicBlueprint: blueprint.is_public,
                    },
                });
                return {
                    result: `Error: The reference video for blueprint "${blueprint.name}" (id ${blueprintId}) could not be read. Ask the user for a different reference.`,
                    isSuccess: false,
                };
            }
            throw error;
        }

        if (referenceAnnotation === null) {
            return {
                result: `Error: The reference video for blueprint "${blueprint.name}" (id ${blueprintId}) has no scenes to imitate. Ask the user for a different reference.`,
                isSuccess: false,
            };
        }

        const customInstructionsSection = blueprint.custom_instructions
            ? `\n\n## Custom Instructions (follow these)\n${blueprint.custom_instructions}`
            : '';
        // The blueprint route has no video id to key on, only the blueprint and its template.
        const exampleKey = `blueprint:${blueprintId}:${exampleTemplateId}`;
        const alreadyServed = this.servedAnnotations.has(exampleKey);
        this.servedAnnotations.add(exampleKey);
        const styleOutcome = applyStyles ? this.applyGlobalStyles(referenceVideoData) : '';
        const styleNote = styleOutcome ? `\n\n${styleOutcome}` : '';
        // The blueprint's settings and instructions are short and steer the build, so they stay on
        // the styling call; only the long annotation is dropped.
        const settingsSection = `\n\n## Blueprint Settings\n${formatBlueprintSettings(blueprint)}${customInstructionsSection}`;

        return {
            result: `Success. Fetched the reference video for blueprint "${blueprint.name}" (id ${blueprintId}).${styleNote}${settingsSection}${bodyForStyleOutcome(styleOutcome, referenceAnnotation, alreadyServed)}`,
            isSuccess: true,
            videoDataSnapshot: referenceVideoData,
            videoDataSnapshotLabel: REFERENCE_SNAPSHOT_LABEL,
        };
    }
}
